import {
  createSlice,
  createAsyncThunk,
  createSelector } from
"@reduxjs/toolkit";
import { ApolloClient, gql, NormalizedCacheObject } from "@apollo/client";
import { RootState } from "@src/redux/store";
import { CreateSubjectListInput, MutationBulkResponseState, SubjectBulkResponse } from "@src/redux/types";

// GraphQL mutation query
const GRAPHQL = gql`
  mutation createSubjectListIterative($data: createSubjectListInput) {
    createSubjectListIterative(data: $data) {
      items {
        referenceId
        name
        response
      }
      error {
        message
        statusCode
      }
    }
  }
`;

// Initial state using the generic MutationResponseState with SubjectBulkResponse
const initialState: MutationBulkResponseState<SubjectBulkResponse> = {
  result: null,
  error: null,
  loading: false
};

// Async thunk for the mutation
export const createSubjectListIterativeRequest = createAsyncThunk<
  SubjectBulkResponse,
  {data: CreateSubjectListInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  "subject/create_subject_list_iterative",
  async (params, { extra: client }) => {
    try {
      const response = await client.mutate({
        mutation: GRAPHQL,
        variables: params
      });
      // Return the mutation result from API response
      return response.data.createSubjectListIterative;
    } catch (error: any) {
      // Reject with error object
      return Promise.reject({
        message: error.message || "An error occurred",
        statusCode: error.statusCode || "500"
      });
    }
  }
);

// Slice definition
const createSubjectListIterativeSlice = createSlice({
  name: "create_subject_list_iterative",
  initialState: initialState,
  reducers: {
    createSubjectListIterativeClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(createSubjectListIterativeRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(
      createSubjectListIterativeRequest.fulfilled,
      (state, action) => {
        state.result = action.payload;
        state.loading = false;
      }
    ).
    addCase(
      createSubjectListIterativeRequest.rejected,
      (state, action) => {
        state.error = action.payload as any || {
          message: "Failed to create subject list iteratively",
          statusCode: "500"
        };
        state.loading = false;
      }
    );
  }
});

// Export actions
export const { createSubjectListIterativeClear } =
createSubjectListIterativeSlice.actions;

// Create selector to get result, error, loading
export const createSubjectListIterativeSelector = createSelector(
  (state: RootState) => state.subject.createSubjectListIterative.result,
  (state: RootState) => state.subject.createSubjectListIterative.error,
  (state: RootState) => state.subject.createSubjectListIterative.loading,
  (result, error, loading) => ({ result, error, loading })
);

// Export reducer
export default createSubjectListIterativeSlice.reducer;